import type { OwnerTerritory } from "../../domain/owner-territories";
import type { TerritoryPreview } from "./useTerritoryPreviews";
import { useI18n } from "../../i18n";
import { cn } from "../cn";

/**
 * TerritoryCard — one derived continuous territory. Displays ONLY authoritative
 * derived fields (area / publishability / GP capacity / property units), plus
 * the persistent Identity's real name + Brand Image thumbnail when present. The
 * label falls back to a neutral display ordinal (NOT a fake persisted name).
 */
export function TerritoryCard({
  ordinal,
  territory,
  preview,
  selected,
  unread = false,
  onOpen,
}: {
  ordinal: number;
  territory: OwnerTerritory;
  preview?: TerritoryPreview;
  selected: boolean;
  unread?: boolean;
  onOpen: (key: string) => void;
}) {
  const { t } = useI18n();
  const fallback = t("dashboard.territory.fallback", { ordinal: String(ordinal).padStart(2, "0") });
  const name = preview?.displayName?.trim() ? preview.displayName : fallback;
  return (
    <button
      type="button"
      data-testid="territory-card"
      data-territory-key={territory.key}
      aria-pressed={selected}
      onClick={() => onOpen(territory.key)}
      className={cn(
        "group w-full text-left rounded-lg p-4",
        "bg-panel border transition-colors duration-fast",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)]",
        selected
          ? "border-border-accent shadow-glow-teal-sm"
          : unread
          ? "border-[color:var(--neon-coral-ring)] hover:bg-elevated"
          : "border-border-soft hover:border-border-accent hover:bg-elevated",
      )}
    >
      <div className="flex items-center gap-4">
        <TerritoryThumb
          url={preview?.thumbnailUrl ?? null}
          ordinal={ordinal}
          named={Boolean(preview?.displayName?.trim())}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            {unread && (
              <span
                data-testid="territory-card-unread-dot"
                className="notif-glow-dot h-2 w-2 shrink-0"
                title={t("dashboard.card.unreadTitle")}
                aria-label={t("dashboard.card.unreadAria")}
              />
            )}
            <span
              data-testid="territory-card-label"
              title={name}
              className="min-w-0 truncate font-display text-[14px] font-semibold tracking-[-0.01em] text-text-primary"
            >
              {name}
            </span>
            <span
              data-testid="territory-card-publishability"
              data-publishable={territory.publishable}
              data-published={Boolean(preview?.published)}
              className={cn(
                "shrink-0 inline-flex items-center h-5 px-2 rounded-sm font-mono text-[9.5px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap",
                preview?.published
                  ? "text-neon-teal border border-border-accent bg-neon-teal-soft"
                  : territory.publishable
                  ? "text-neon-teal border border-border-accent"
                  : "text-text-muted border border-border-soft",
              )}
            >
              {preview?.published
                ? t("dashboard.publishability.published")
                : territory.publishable
                ? t("dashboard.publishability.publishable")
                : t("dashboard.publishability.notPublishable")}
            </span>
          </div>

          <dl className="mt-3 flex flex-wrap gap-x-7 gap-y-2">
        <div>
          <dt className="text-[9.5px] uppercase tracking-[0.16em] text-text-muted font-mono">
            {t("dashboard.card.area")}
          </dt>
          <dd
            data-testid="territory-card-area"
            className="mt-0.5 font-mono text-[14px] text-text-primary"
          >
            {territory.area.toLocaleString()} u²
          </dd>
        </div>
        <div>
          <dt className="text-[9.5px] uppercase tracking-[0.16em] text-text-muted font-mono">
            {t("dashboard.card.gpCapacity")}
          </dt>
          <dd
            data-testid="territory-card-gp"
            className="mt-0.5 font-mono text-[14px] text-neon-teal"
          >
            {territory.capacity} GP
          </dd>
        </div>
        <div>
          <dt className="text-[9.5px] uppercase tracking-[0.16em] text-text-muted font-mono">
            {t("dashboard.card.propertyUnits")}
          </dt>
          <dd
            data-testid="territory-card-units"
            className="mt-0.5 font-mono text-[14px] text-text-primary"
          >
            {territory.unitCount}
          </dd>
        </div>
      </dl>

      {!territory.publishable && (
        <p
          data-testid="territory-card-min-note"
          className="mt-3 text-[11px] text-text-muted"
        >
          {t("dashboard.card.minNote")}
        </p>
      )}
        </div>
      </div>
    </button>
  );
}

/**
 * TerritoryThumb — small square preview. Shows the Identity Brand Image when
 * present; otherwise a neutral, on-brand placeholder (glass square + grid glyph
 * + ordinal) so the list stays harmonious even before an image is set.
 */
function TerritoryThumb({
  url,
  ordinal,
  named,
}: {
  url: string | null;
  ordinal: number;
  named: boolean;
}) {
  if (url) {
    return (
      <img
        data-testid="territory-card-thumb"
        src={url}
        alt=""
        draggable={false}
        className="h-14 w-14 shrink-0 rounded-md object-cover border border-border-soft bg-obsidian"
      />
    );
  }
  return (
    <div
      data-testid="territory-card-thumb-placeholder"
      aria-hidden
      className={cn(
        "h-14 w-14 shrink-0 rounded-md border border-border-soft",
        "flex items-center justify-center relative overflow-hidden",
      )}
      style={{
        background:
          "linear-gradient(150deg, rgba(255,255,255,0.05) 0%, rgba(34,211,238,0.06) 100%)",
      }}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
        stroke="currentColor" strokeWidth="1" className="text-text-muted">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
      </svg>
      {!named && (
        <span className="absolute bottom-0.5 right-1 font-mono text-[8px] text-text-faint tabular-nums">
          {String(ordinal).padStart(2, "0")}
        </span>
      )}
    </div>
  );
}
